import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { MsalService } from "@azure/msal-angular";

import { NotificationService } from "./notification.service";

/**
 * M-06 — Timeout absoluto de sessão (ASVS V7.3).
 *
 * Complementa o timeout de inatividade: independentemente de atividade, a sessão
 * expira 8h após o login. O marco (`loginTimestamp`) é gravado no `window.sessionStorage`
 * (isolado por aba e volátil, coerente com a decisão do M-05 de usar SessionStorage no MSAL)
 * e sobrevive a reloads da mesma aba.
 *
 * A verificação é feita a cada request HTTP (via SessionTimeoutInterceptor) e pode ser
 * chamada em navegação. Ao expirar, força logout no MSAL e redireciona para /login.
 */
@Injectable({
    providedIn: "root"
})
export class SessionTimeoutService {
    /** Timeout absoluto: 8 horas em milissegundos. */
    public static readonly ABSOLUTE_TIMEOUT_MS = 8 * 60 * 60 * 1000;

    /** Antecedência do aviso ao usuário antes do timeout absoluto: 15 minutos. */
    public static readonly WARNING_BEFORE_MS = 15 * 60 * 1000;

    private static readonly STORAGE_KEY = "aias.session.loginTimestamp";

    private router = inject(Router);
    private msalService = inject(MsalService);
    private notification = inject(NotificationService);

    /** Evita disparar o aviso e o logout repetidamente a cada request. */
    private warningShown = false;
    private expiring = false;

    /**
     * Marca o início da sessão. Idempotente: se já existe um marco válido nesta aba,
     * preserva-o (não reinicia o relógio a cada verificação/render). Chamar quando uma
     * conta ativa do MSAL for confirmada após o login/redirect.
     */
    startSession(): void {
        if (!this.isBrowserStorageAvailable()) return;

        const existing = this.readTimestamp();
        if (existing !== null && !this.isExpired(existing)) {
            return;
        }
        this.resetSession();
    }

    /** (Re)inicia o relógio da sessão a partir de agora. */
    resetSession(): void {
        if (!this.isBrowserStorageAvailable()) return;
        try {
            window.sessionStorage.setItem(SessionTimeoutService.STORAGE_KEY, Date.now().toString());
        } catch {
            // Storage indisponível (modo restrito): timeout absoluto degrada para no-op nesta aba.
        }
        this.warningShown = false;
        this.expiring = false;
    }

    /** Remove o marco da sessão (usar no logout explícito). */
    clearSession(): void {
        if (!this.isBrowserStorageAvailable()) return;
        try {
            window.sessionStorage.removeItem(SessionTimeoutService.STORAGE_KEY);
        } catch {
            // no-op
        }
        this.warningShown = false;
        this.expiring = false;
    }

    /**
     * Verifica o timeout absoluto. Se expirado, força o encerramento da sessão e retorna true.
     * Se estiver dentro da janela de aviso, exibe o aviso uma única vez. Sem marco → não faz nada.
     */
    checkTimeout(): boolean {
        const loginTimestamp = this.readTimestamp();
        if (loginTimestamp === null) {
            return false;
        }

        if (this.isExpired(loginTimestamp)) {
            this.expireSession();
            return true;
        }

        const remaining = SessionTimeoutService.ABSOLUTE_TIMEOUT_MS - (Date.now() - loginTimestamp);
        if (remaining <= SessionTimeoutService.WARNING_BEFORE_MS && !this.warningShown) {
            this.warningShown = true;
            // Mensagem traduzível; usa o serviço de notificação padrão do projeto.
            this.notification.info("general.messages.sessionExpiringSoon", true);
        }

        return false;
    }

    private isExpired(loginTimestamp: number): boolean {
        return Date.now() - loginTimestamp >= SessionTimeoutService.ABSOLUTE_TIMEOUT_MS;
    }

    private readTimestamp(): number | null {
        if (!this.isBrowserStorageAvailable()) return null;
        let raw: string | null = null;
        try {
            raw = window.sessionStorage.getItem(SessionTimeoutService.STORAGE_KEY);
        } catch {
            return null;
        }
        if (!raw) return null;
        const parsed = Number(raw);
        // Valor corrompido → tratar como sem marco (fail-safe: não expira nem trava).
        return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    }

    private expireSession(): void {
        // Evita múltiplos logouts concorrentes (vários requests podem expirar juntos).
        if (this.expiring) return;
        this.expiring = true;

        this.clearSession();
        this.notification.info("general.messages.sessionExpired", true);

        const returnUrl = this.router.url;
        // Usa a instância do MSAL (mesmo padrão de AzureAdService.logout), que dispara o redirect
        // imediatamente (Promise), sem depender de subscribe. logoutRedirect encerra a sessão no
        // MSAL e o postLogoutRedirectUri traz de volta ao app, onde o guard/login assume o fluxo.
        // Fallback para navegação local se o logout falhar.
        try {
            this.msalService.instance.logoutRedirect({
                postLogoutRedirectUri: window.location.origin
            });
        } catch {
            this.router.navigate(["/login"], { queryParams: { returnUrl } });
        }
    }

    private isBrowserStorageAvailable(): boolean {
        return typeof window !== "undefined" && !!window.sessionStorage;
    }
}
